import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Globe, RefreshCw, Play, Square, ArrowRight, User as UserIcon,
  Pencil, CheckCircle2, AlertTriangle, Clock, Download, Upload, X
} from "lucide-react";

// =============================================================================
// SurveydraftsActivityPanel — Dashboard tile combining:
//   1. Recent Activity feed (cached SD timeline events across all jobs)
//   2. Divergence Alerts (jobs whose LD stage ≠ SD status) + force push/pull
// =============================================================================
export default function SurveydraftsActivityPanel() {
  const [activity, setActivity] = useState(null);
  const [recon, setRecon] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [loadingRecon, setLoadingRecon] = useState(false);
  const [showRecon, setShowRecon] = useState(false);

  const loadActivity = (warm = false) => {
    setLoadingActivity(true);
    // Try the live (real-time) global feed first — falls back to cached if SD unreachable
    api.get("/integrations/surveydrafts/live-activity", { params: { limit: 25 } })
      .then(({ data }) => {
        if (data?.ok && data.events?.length) {
          setActivity({ ok: true, events: data.events, source: "live" });
          setLoadingActivity(false);
          return;
        }
        return api.get("/integrations/surveydrafts/recent-activity", { params: { limit: 15, warm } })
          .then(({ data: cached }) => setActivity({ ...cached, source: "cache" }));
      })
      .catch(() => setActivity({ ok: false, events: [], source: "error" }))
      .finally(() => setLoadingActivity(false));
  };

  const loadRecon = () => {
    setLoadingRecon(true);
    api.get("/integrations/surveydrafts/reconciliation", { params: { limit: 100 } })
      .then(({ data }) => setRecon(data))
      .catch(() => setRecon({ ok: false, divergences: [] }))
      .finally(() => setLoadingRecon(false));
  };

  useEffect(() => {
    loadActivity(false);
    loadRecon();
  }, []);

  const divCount = recon?.divergences?.length || 0;

  return (
    <div className="brut-card p-5 space-y-4" data-testid="sd-activity-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-mono flex items-center gap-1.5 text-zinc-500">
            <Globe className="w-3 h-3" /> SURVEYDRAFTS.US · LIVE FEED
          </div>
          <h3 className="font-heading text-xl font-black tracking-tight">Recent Activity &amp; Divergence</h3>
          {activity?.source && (
            <div className="label-mono text-[10px] text-zinc-500 mt-0.5">
              feed source: <b className={activity.source === "live" ? "text-emerald-700" : "text-amber-700"}>{activity.source.toUpperCase()}</b>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {divCount > 0 && (
            <button
              onClick={() => setShowRecon(true)}
              className="brut-btn px-3 py-1.5 text-xs flex items-center gap-1 bg-amber-300 hover:bg-amber-400"
              data-testid="sd-recon-open-btn"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> {divCount} DIVERGENCE{divCount > 1 ? "S" : ""}
            </button>
          )}
          <button
            onClick={() => { loadActivity(true); loadRecon(); }}
            className="brut-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1"
            data-testid="sd-activity-refresh-btn"
            title="Warm cache for 20 latest jobs + recheck divergence"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingActivity ? "animate-spin" : ""}`} /> REFRESH
          </button>
        </div>
      </div>

      {/* Divergence summary line */}
      <div className="flex items-center gap-4 text-xs font-mono flex-wrap">
        {loadingRecon ? (
          <span className="text-zinc-500">Scanning {recon?.scanned || "…"} jobs for divergence…</span>
        ) : recon?.ok === false ? (
          <span className="text-amber-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {recon.reason || "Reconciliation unavailable"}
          </span>
        ) : (
          <>
            <span className="text-zinc-600">
              ✓ Scanned <b className="text-zinc-900">{recon?.scanned ?? 0}</b> SD jobs ·
            </span>
            <span className={divCount === 0 ? "text-emerald-700 font-bold" : "text-amber-700 font-bold"}>
              {divCount === 0 ? "All in sync." : `${divCount} stage mismatch${divCount > 1 ? "es" : ""}`}
            </span>
          </>
        )}
      </div>

      {/* Activity feed */}
      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="sd-activity-list">
        {loadingActivity && (
          <div className="text-xs font-mono text-zinc-500 py-6 text-center">Loading activity feed…</div>
        )}
        {!loadingActivity && (!activity?.events || activity.events.length === 0) && (
          <div className="text-xs font-mono text-zinc-500 py-6 text-center">
            No cached events yet — click <strong>REFRESH</strong> above to warm the cache.
          </div>
        )}
        {activity?.events?.map((e, i) => <ActivityRow key={e.id || i} e={e} />)}
      </div>

      {showRecon && <ReconciliationModal recon={recon} onClose={() => setShowRecon(false)} onResolved={loadRecon} />}
    </div>
  );
}

const ACTION_META = {
  timer_start:    { icon: Play,         tone: "bg-emerald-100", label: "STARTED TIMER" },
  timer_stop:     { icon: Square,       tone: "bg-rose-100",    label: "STOPPED TIMER" },
  status_change:  { icon: ArrowRight,   tone: "bg-yellow-100",  label: "STATUS" },
  assignment:     { icon: UserIcon,     tone: "bg-indigo-100",  label: "ASSIGNED" },
  assignment_change: { icon: UserIcon,  tone: "bg-indigo-100",  label: "REASSIGNED" },
  note_added:     { icon: Pencil,       tone: "bg-zinc-100",    label: "NOTE" },
  completed:      { icon: CheckCircle2, tone: "bg-emerald-200", label: "COMPLETED" },
};

function ActivityRow({ e }) {
  const meta = ACTION_META[e.action_type] || { icon: Clock, tone: "bg-zinc-100", label: (e.action_type || "EVENT").toUpperCase().replace(/_/g, " ") };
  const Icon = meta.icon;
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = Date.now();
      const diff = (now - d.getTime()) / 1000;
      if (diff < 60) return `${Math.floor(diff)}s ago`;
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <Link
      to={`/jobs`}
      className="flex items-start gap-3 p-2 border border-zinc-200 hover:bg-yellow-50 transition-colors text-left"
      data-testid={`sd-event-${e.id || e.job_id}`}
    >
      <span className={`w-7 h-7 border border-zinc-900 ${meta.tone} flex items-center justify-center shrink-0 mt-0.5`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="label-mono">{meta.label}</span>
          {e.job_number && (
            <span className="font-mono text-[10px] text-zinc-500 truncate" title={e.project_name || e.job_number}>
              · {e.job_number}
            </span>
          )}
          {e.vendor_name && (
            <span className="px-1 py-0.5 bg-zinc-100 border border-zinc-300 text-[9px] font-mono truncate max-w-[180px]" title={e.vendor_name}>
              {e.vendor_name}
            </span>
          )}
        </div>
        <div className="text-xs font-mono text-zinc-600 mt-0.5 truncate" title={e.description}>
          {e.description || "—"}
        </div>
        <div className="text-[10px] font-mono text-zinc-500 mt-0.5 flex items-center gap-2">
          {e.performed_by_name && (
            <span className="flex items-center gap-1">
              <UserIcon className="w-2.5 h-2.5" /> {e.performed_by_name}
            </span>
          )}
          <span>·</span>
          <span>{fmt(e.created_at)}</span>
        </div>
      </div>
    </Link>
  );
}

// =============================================================================
// Divergence reconciliation modal
// =============================================================================
function ReconciliationModal({ recon, onClose, onResolved }) {
  const [rows, setRows] = useState(recon?.divergences || []);
  const [busy, setBusy] = useState({});

  const act = async (job, direction) => {
    setBusy((m) => ({ ...m, [job.job_id]: direction }));
    try {
      const { data } = await api.post(`/integrations/surveydrafts/reconcile/${job.job_id}`, null, {
        params: { direction },
      });
      if (data.ok) {
        toast.success(`${direction === "push" ? "Pushed to" : "Pulled from"} surveydrafts.us — ${data.to_stage || data.pushed_to_status}`);
        setRows((rs) => rs.filter((r) => r.job_id !== job.job_id));
        onResolved && onResolved();
      } else {
        toast.error(data.reason || data.message || "Resolve failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolve failed");
    }
    setBusy((m) => { const c = { ...m }; delete c[job.job_id]; return c; });
  };

  return (
    <div
      className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-3"
      onClick={onClose}
      data-testid="recon-overlay"
    >
      <div
        className="brut-card max-w-4xl w-full flex flex-col"
        style={{ maxHeight: "calc(100vh - 24px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-3 border-b border-zinc-900 bg-white">
          <div>
            <div className="label-mono text-zinc-500 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> RECONCILIATION
            </div>
            <h2 className="font-heading text-xl font-black">Stage divergence · {rows.length} job{rows.length !== 1 ? "s" : ""}</h2>
            <p className="text-xs font-mono text-zinc-600 mt-0.5">
              <b>PUSH</b> = Land Drafters overrides surveydrafts.us · <b>PULL</b> = surveydrafts.us overrides Land Drafters
            </p>
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5" data-testid="recon-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0">
          {rows.length === 0 ? (
            <div className="px-5 py-10 text-center text-emerald-700 font-mono text-sm">
              ✓ All clear — every SD job is currently in sync.
            </div>
          ) : (
            <table className="w-full text-xs font-mono" data-testid="recon-table">
              <thead className="bg-zinc-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-2 border-b border-zinc-300">JOB</th>
                  <th className="text-left px-4 py-2 border-b border-zinc-300">VENDOR</th>
                  <th className="text-left px-4 py-2 border-b border-zinc-300">LAND DRAFTERS</th>
                  <th className="text-left px-4 py-2 border-b border-zinc-300">SURVEYDRAFTS</th>
                  <th className="text-right px-4 py-2 border-b border-zinc-300">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.job_id} className="border-b border-zinc-200 hover:bg-yellow-50" data-testid={`recon-row-${r.job_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-bold">{r.job_number}</div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[200px]" title={r.project_name || r.client}>
                        {r.client || r.project_name || ""}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-zinc-700">{r.vendor_name || "—"}</td>
                    <td className="px-4 py-2">
                      <span className="bg-yellow-100 border border-zinc-900 px-1.5 py-0.5">{r.ld_stage}</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="bg-emerald-100 border border-zinc-900 px-1.5 py-0.5">{r.sd_status}</span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => act(r, "push")}
                          disabled={!!busy[r.job_id]}
                          className="brut-btn-secondary px-2 py-1 text-[10px] flex items-center gap-1 disabled:opacity-50"
                          data-testid={`recon-push-${r.job_id}`}
                          title="Push LD stage to surveydrafts.us"
                        >
                          <Upload className="w-3 h-3" /> {busy[r.job_id] === "push" ? "…" : "PUSH"}
                        </button>
                        <button
                          onClick={() => act(r, "pull")}
                          disabled={!!busy[r.job_id]}
                          className="brut-btn-secondary px-2 py-1 text-[10px] flex items-center gap-1 disabled:opacity-50"
                          data-testid={`recon-pull-${r.job_id}`}
                          title="Pull SD status into Land Drafters"
                        >
                          <Download className="w-3 h-3" /> {busy[r.job_id] === "pull" ? "…" : "PULL"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
